import React, { type ForwardedRef, forwardRef, type ReactNode, useCallback } from 'react';

import { Portal, PortalArea } from '@pkg/components';
import {
    type KeyPressCallback,
    type OutsideClickCallback,
    type OutsideClickExclusion,
    useInstantRef,
    useKeyPress,
    useOutsideClick,
} from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';
import { noop } from '@pkg/utils';

import { useLockBodyScroll } from '@app/helpers';

import {
    sheetDesktopHidden,
    sheetDesktopShow,
    sheetDesktopTuck,
    sheetMobileHidden,
    sheetMobileShow,
    sheetTransition,
} from './Sheet.motion';
import { type SheetSize } from './Sheet.types';
import { ActionToast, FixedCloseAction, Footer, Header, Layout, Main } from './components';

import { Backdrop, Card, Interior, StyledSheet } from './styles';

export interface SheetProps {
    children: ReactNode;
    size?: SheetSize;
    backdrop?: boolean;
    tuck?: boolean;
    closeExclusions?: OutsideClickExclusion;
    onClickOutside?: OutsideClickCallback;
    onClose?: () => void;
    // TODO: Remove `gapless` after we audit all `Sheet` instances
    // and can safely revise the layout styling.
    gapless?: boolean;
}

function getSheetAnimation(enoughWindowWidth = false, tuck = false) {
    // NOTE: Currently, `tuck` is a "tablet and up" prop.
    // If we want to support mobile, we need a shorter `tuck` distance.
    if (enoughWindowWidth && tuck) return sheetDesktopTuck;

    return enoughWindowWidth ? sheetDesktopShow : sheetMobileShow;
}

// TODO: Leverage `useInertPage()` for "focus trapping".
function SheetComponent(
    {
        children,
        size = 'medium',
        backdrop = false,
        tuck = false,
        gapless = false,
        closeExclusions,
        onClose,
        onClickOutside,
    }: SheetProps,
    cardRef: ForwardedRef<HTMLDivElement>,
) {
    const { tablet, desktop } = useMediaQuery();
    const [wrapperElement, wrapperRef] = useInstantRef<HTMLDivElement>();

    const breakpointReached = size === 'wide' ? desktop : tablet;

    // TODO: Replace this with `useScrollLock()` from:
    // https://github.com/beefchimi/react-hooks/pull/47
    useLockBodyScroll(true);

    const handleEscape: KeyPressCallback = useCallback(
        (event) => {
            event.preventDefault();
            onClose?.();
        },
        [onClose],
    );

    useKeyPress(['Escape'], handleEscape, {
        // TODO: Scope this to the `wrapperElement` once we have
        // focus trapping. Until then, `Escape` cannot be reliably
        // captured from that `target`.
        disabled: !onClose,
    });

    // TODO: If we never implmenet a "drawer-like" mobile layout,
    // then we should qualify the callback with `breakpointReached`.
    useOutsideClick(wrapperElement, onClickOutside ?? noop, {
        disabled: !onClickOutside,
        exclude: closeExclusions,
    });

    // TODO: Since we want "collapsible behaviour", using `onOutsideClick()`
    // needs to prevent the `onClose` and consumer's need to call that themselves.
    const backdropMarkup =
        backdrop && breakpointReached ? (
            <Backdrop key="Sheet-Backdrop" onClick={onClickOutside ? undefined : onClose} />
        ) : null;

    return (
        <Portal destination={PortalArea.Transition}>
            {/*
                TODO: Fix bug where `backdrop` has no "exit animation" when
                toggled `false` while rendered.
            */}
            {backdropMarkup}

            <StyledSheet
                key={`Sheet-${breakpointReached}`}
                ref={wrapperRef}
                initial={breakpointReached ? sheetDesktopHidden : sheetMobileHidden}
                exit={breakpointReached ? sheetDesktopHidden : sheetMobileHidden}
                animate={getSheetAnimation(breakpointReached, tuck)}
                transition={sheetTransition}
                $size={size}
                aria-modal
            >
                <Card ref={cardRef}>
                    <Interior $gapless={gapless}>{children}</Interior>
                </Card>
            </StyledSheet>
        </Portal>
    );
}

const SheetForwarded = forwardRef(SheetComponent);

export const Sheet = Object.assign(SheetForwarded, {
    ActionToast,
    FixedCloseAction,
    Footer,
    Header,
    Layout,
    Main,
});
